<div class="min-h-screen bg-background">
  <!-- Global main header -->
  <!-- Left edge follows the sidebar. Both widths are bound rather than one being a static class,
       because two same-specificity Tailwind classes would be settled by stylesheet order, not by
       which one is meant to apply. -->
  <header class="fixed top-0 left-0 right-0 h-14 bg-sidebar border-b border-sidebar-border flex items-center justify-between px-4 z-50 transition-all duration-300"
    [class.lg:left-60]="!sidebarCollapsed()" [class.lg:left-16]="sidebarCollapsed()">
    <div class="flex items-center gap-3">
      <button
        type="button"
        (click)="toggleMobileNav()"
        class="p-2 rounded-lg hover:bg-muted transition-colors lg:hidden"
        aria-label="Toggle navigation">
        @if (showMobileNav()) {
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        } @else {
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        }
      </button>
      <img src="hashbooks-logo.png" alt="HashBooks" class="h-7 lg:hidden" />
    </div>

    <div class="flex items-center gap-2">
      @if (subscriptionModeEnabled()) {
        <button
          type="button"
          (click)="goToPricing()"
          class="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/60 transition-colors capitalize"
          aria-label="Manage plan">
          <span class="h-1.5 w-1.5 rounded-full bg-primary"></span>
          {{ planCode() }}
        </button>
      }
      <div class="relative">
        <button
          type="button"
          (click)="toggleNotifications()"
          class="relative p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/60 transition-all duration-150"
          aria-label="Notifications">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          @if (unreadCount() > 0) {
            <span class="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
              {{ unreadCount() > 9 ? '9+' : unreadCount() }}
            </span>
          }
        </button>
      </div>

      <div class="relative">
        <button
          type="button"
          (click)="toggleProfileMenu()"
          class="flex items-center gap-2 pl-1 pr-2 py-1.5 rounded-lg hover:bg-muted/60 transition-colors">
          <div class="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium text-xs shrink-0">
            {{ userInitials() }}
          </div>
          <div class="hidden sm:block text-left">
            <p class="text-sm font-medium text-foreground leading-tight">{{ userName() }}</p>
            <p class="text-xs text-muted-foreground leading-tight">{{ userEmail() }}</p>
          </div>
        </button>
      </div>
    </div>
  </header>

  @if (notificationsOpen()) {
    <div class="fixed inset-0 z-30" (click)="closeNotifications()"></div>
    <div class="fixed top-14 right-4 lg:right-5 mt-2 w-80 bg-popover border border-border rounded-lg shadow-lg z-[60] animate-in">
          <div class="p-4 border-b border-border flex items-center justify-between">
            <h4 class="font-semibold text-foreground">Notifications</h4>
            <button type="button" (click)="onMarkAllRead()" class="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Mark all read
            </button>
          </div>
          <div class="max-h-80 overflow-y-auto scroll-thin">
            @if (notifications().length === 0) {
              <div class="p-8 text-center">
                <div class="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <svg class="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                </div>
                <p class="text-sm font-medium text-foreground">You're all caught up</p>
                <p class="text-xs text-muted-foreground mt-1">No new notifications right now.</p>
              </div>
            } @else {
              @for (n of notifications(); track n.id) {
                <div (click)="onNotificationClick(n)"
                  class="p-4 border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                  [class.bg-muted]="!n.read">
                  <div class="flex gap-3">
                    <div class="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      @switch (n.kind) {
                        @case ('sync_complete') {
                          <!-- check-circle · green -->
                          <svg class="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
                        }
                        @case ('sync_failed') {
                          <!-- alert-triangle · red -->
                          <svg class="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        }
                        @case ('reauth_needed') {
                          <!-- lock · amber -->
                          <svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        }
                        @case ('new_device_login') {
                          <!-- monitor · blue -->
                          <svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                        }
                        @case ('welcome') {
                          <!-- gift · purple -->
                          <svg class="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
                        }
                        @case ('issue_completed') {
                          <!-- check-circle · green -->
                          <svg class="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
                        }
                        @case ('issue_need_more_information') {
                          <!-- help-circle · amber — this one needs the user to act -->
                          <svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        }
                        @case ('issue_submitted') {
                          <!-- message-square · teal -->
                          <svg class="w-4 h-4 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        }
                        @case ('issue_assigned') {
                          <!-- user · blue -->
                          <svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        }
                        @case ('issue_in_progress') {
                          <!-- clock · blue -->
                          <svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        }
                        @case ('issue_reopened') {
                          <!-- refresh-cw · amber -->
                          <svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                        }
                        @case ('issue_closed') {
                          <!-- check · muted -->
                          <svg class="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        }
                        @case ('spam_shield') {
                          <!-- shield-check · emerald -->
                          <svg class="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                        }
                        @case ('subscription_activated') {
                          <!-- credit-card · primary -->
                          <svg class="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                        }
                        @default {
                          <!-- bell · muted -->
                          <svg class="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                        }
                      }
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-foreground">{{ n.title }}</p>
                      <p class="text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words">{{ n.description }}</p>
                      <p class="text-xs text-muted-foreground mt-1">{{ n.time }}</p>
                    </div>
                    @if (!n.read) {
                      <span class="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" aria-label="Unread"></span>
                    }
                  </div>
                </div>
              }
            }
          </div>
    </div>
  }

  @if (profileMenuOpen()) {
    <div class="fixed inset-0 z-30" (click)="closeProfileMenu()"></div>
    <div class="fixed top-14 right-4 lg:right-5 mt-2 w-56 bg-popover border border-border rounded-lg shadow-lg z-[60] py-1 animate-in">
      <button type="button" (click)="onIntegrations()" class="w-full flex items-center px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
        <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8v4a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8Z"/></svg>
        Integrations
      </button>
      <button type="button" (click)="onSettings()" class="w-full flex items-center px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
        <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        Settings
      </button>
      <div class="my-1 border-t border-border"></div>
      <button type="button" (click)="onLogout()" class="w-full flex items-center px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors">
        <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        Log out
      </button>
    </div>
  }

  <!-- Mobile overlay -->
  @if (showMobileNav()) {
    <div
      class="fixed inset-0 bg-black/50 z-40 lg:hidden"
      (click)="closeMobileNav()"
      aria-hidden="true">
    </div>
  }

  <!-- Sidebar (fixed) -->
  <app-shell-sidebar
    [groups]="navGroups"
    [mobileOpen]="showMobileNav()"
    (itemSelected)="closeMobileNav()">
  </app-shell-sidebar>

  <!-- Main content (offset for sidebar on lg+) -->
  <div class="pt-14 transition-all duration-300"
    [class.lg:ml-60]="!sidebarCollapsed()" [class.lg:ml-16]="sidebarCollapsed()">
    <main class="p-4 md:p-6">
      <router-outlet />
    </main>
  </div>
</div>

<!-- Global floating sync status widget -->
<app-sync-status></app-sync-status>

<!-- Speed-dial: the chat + report FABs only render when the arrow toggle is open, so a single
     arrow button shows at rest. The widgets keep their own fixed positions (chat bottom:80px,
     report bottom:144px) and stack above the arrow (bottom:24px). -->
@if (fabOpen()) {
<app-chat-widget></app-chat-widget>
<app-report-issue-widget></app-report-issue-widget>
}

<!-- Arrow toggle FAB — the only thing visible at rest. Click to reveal chat + report. Hidden while a
     dialog is open: it sits above every overlay and would cover the dialog's own buttons on a phone. -->
@if (!scrollLock.locked()) {
<button type="button" (click)="toggleFab()"
  class="fab-arrow group fixed z-[1001] grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-900/25 ring-1 ring-white/25 backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-900/30 hover:ring-white/40 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
  style="bottom: 24px; right: 24px;"
  [attr.aria-label]="fabOpen() ? 'Hide chat and report options' : 'Show chat and report options'"
  [attr.aria-expanded]="fabOpen()">
  <svg class="h-[18px] w-[18px] transition-transform duration-300 ease-out group-hover:scale-110" [class.rotate-180]="fabOpen()" fill="none"
    stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
  </svg>
</button>
}

<!-- Global referral popup (opened from the sidebar "Earn up to $500" item) -->
<app-referral-modal></app-referral-modal>
